import { ENDPOINTS } from "../config/api";

type TResponseLogin = {
  access_token: string;
  refresh_token: string;
  token_type: string;
};

type TResponseRefreshToken = {
  access_token: string;
  refresh_token: string;
  token_type: string;
};

type TResponseLogout = {};

type TUserRole = {
  name: string;
  description: string;
  id: string;
};

type TStatisticsHistoryItem = {
  user_id: string
  error_count: number
  keystrokes_count: number
  text_length: number
  typing_speed: number
  accuracy: number
  error_rate: number
  elapsed_time: number
  created_at: string
};

type TResponseGetUser = {
  id: string,
  username: string,
  created_at: string,
  roles: TUserRole[]
};

type TResponseStatisticsHistoryItem = {
  items: TStatisticsHistoryItem[];
  count: number;
};

type TApiError = Error & {
  status: number;
  details?: unknown;
};

const getAccessToken = (): string => localStorage.getItem("accessToken") || "";

const setTokens = (tokens: { refresh_token: string; access_token: string }): void => {
  localStorage.setItem("refreshToken", tokens.refresh_token);
  localStorage.setItem("accessToken", tokens.access_token);
};

const clearTokens = (): void => {
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessToken");
};

const buildAuthHeaders = (headers?: HeadersInit): Headers => {
  const mergedHeaders = new Headers(headers);
  const accessToken = getAccessToken();

  if (accessToken) {
    mergedHeaders.set("Authorization", `Bearer ${accessToken}`);
  }

  return mergedHeaders;
};

export async function getResponse<T>(res: Response): Promise<T> {
  if (res.ok) {
    if (res.status === 204) {
      return undefined as T;
    }

    return res.json();
  }

  const contentType = res.headers.get("content-type") || "";
  let details: unknown;

  try {
    details = contentType.includes("application/json")
      ? await res.json()
      : await res.text();
  } catch {
    details = undefined;
  }

  const errorMessage =
    typeof details === "string" && details.length > 0
      ? details
      : `Error ${res.status}`;

  const error = new Error(errorMessage) as TApiError;
  error.status = res.status;
  error.details = details;

  throw error;
}

export const refreshToken = (): Promise<TResponseRefreshToken> => {
  return fetch(ENDPOINTS.REFRESH_TOKEN, {
    method: "POST",
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify({
      refresh_token: localStorage.getItem("refreshToken"),
    }),
  })
    .then(getResponse<TResponseRefreshToken>)
    .then((refreshData) => {
      if (!refreshData.access_token) {
        return Promise.reject(refreshData);
      }
      setTokens(refreshData);
      return refreshData;
    });
};

export const fetchWithRefresh = async <T>(
  url: string,
  options: RequestInit = {},
  requiresAuth = false
): Promise<T> => {
  const requestOptions = requiresAuth
    ? { ...options, headers: buildAuthHeaders(options.headers) }
    : options;
  const res = await fetch(url, requestOptions);

  if (!requiresAuth || res.status !== 401) {
    return getResponse<T>(res);
  }

  await refreshToken();

  const retryOptions = { ...options, headers: buildAuthHeaders(options.headers) };
  const retryRes = await fetch(url, retryOptions);
  return getResponse<T>(retryRes);
};

export const loginRequest = async (
  email: string,
  password: string
): Promise<TResponseLogin> => {
  const body = new URLSearchParams({
    username: email,
    password: password,
  });

  return fetchWithRefresh<TResponseLogin>(ENDPOINTS.LOGIN, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: body.toString(),
  }).then((refreshData) => {
    localStorage.setItem("refreshToken", refreshData.refresh_token);
    localStorage.setItem("accessToken", refreshData.access_token);

    return refreshData;
  });
};

export const loginRequestGoogle = async (): Promise<TResponseLogin> => {
  return fetchWithRefresh<TResponseLogin>(ENDPOINTS.LOGIN_GOOGLE, {
    method: "GET",
  });
};

export const loginRequestGoogleCode = async (queryString: string): Promise<TResponseLogin> => {
  return fetchWithRefresh<TResponseLogin>(`${ENDPOINTS.LOGIN_GOOGLE_CODE}${queryString}`, {
    method: "GET",
  }).then((refreshData) => {
    localStorage.setItem("refreshToken", refreshData.refresh_token);
    localStorage.setItem("accessToken", refreshData.access_token);

    return refreshData;
  });
};

export const logoutRequest = (): Promise<TResponseLogout> => {
  return fetch(ENDPOINTS.LOGOUT, {
    method: "POST",
    headers: buildAuthHeaders({
      "Content-Type": "application/json",
    }),
  }).then((res) => {
    clearTokens();

    return getResponse<TResponseLogout>(res);
  }).catch((err) => {
    clearTokens();

    return Promise.reject(err);
  });
};

export const getUserRequest = async (): Promise<TResponseGetUser> => {
  try {
    return await fetchWithRefresh<TResponseGetUser>(ENDPOINTS.USER, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }, true);
  } catch (err) {
    clearTokens();

    throw err;
  }
};

export const getStatisticsHistoryRequest = async (userId: string, offset: number, pageSize: number): Promise<TResponseStatisticsHistoryItem> => {
  try {
    return await fetchWithRefresh<TResponseStatisticsHistoryItem>(ENDPOINTS.STATISTICS_HISTORY(userId, offset, pageSize), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    }, true);
  } catch (err) {
    clearTokens();

    throw err;
  }
};
